/**
 * UndoManager
 *
 * Manages undo/redo stacks and keyboard shortcuts (Ctrl+Z / Ctrl+Shift+Z).
 * Singleton — shared across the editor via getUndoManager().
 *
 * Public API is ref-based: commands hold SceneObject references, not paths.
 * The manager exposes a `traceScene` reference used by the few commands
 * (Create/Delete) that must create new SceneObjects from serialized data.
 *
 * ## Keyboard shortcuts
 *
 * Keyboard shortcut wiring is **opt-in** via `attachKeyboardShortcuts()`.
 * The singleton returned from `getUndoManager()` attaches them automatically
 * the first time it's created; tests that `new UndoManager()` directly get
 * a clean instance with no `window` listeners (important — each
 * `beforeEach` would otherwise leak a persistent capture-phase listener).
 */

import type { UndoCommand } from './UndoCommand';
import type { TraceScene } from '../../core/TraceScene';
import { installLayoutInspectorRefresh } from '../layout-bindings';
import { installLayoutTopologySync, settleLayouts } from '../layoutTopologySync';
import { Logger } from '../../utils/Logger';

const MAX_DEPTH = 50;

export class UndoManager {
    private undoStack: UndoCommand[] = [];
    private redoStack: UndoCommand[] = [];
    private _isUndoRedoing = false;
    private _frozen = false;
    private _pendingPartial: { command: UndoCommand; stack: 'undo' | 'redo' } | null = null;
    private _onChangeCallbacks: Array<(depthShifted: boolean) => void> = [];
    /**
     * Fired immediately after a command is pushed (including after a
     * successful coalesce). Replay recorders subscribe here so they can
     * serialize the top-of-stack command the instant it becomes part of
     * user history. Not fired during undo/redo (those don't create new
     * history) and not fired when frozen.
     */
    private _onPushCallbacks: Array<(cmd: UndoCommand, coalesced: boolean) => void> = [];

    private _traceScene: TraceScene | null = null;
    /** Detach hook for the editor bridges installed on the current scene. */
    private _detachSceneBridges: (() => void) | null = null;

    /** The current TraceScene — set on init, updated on scene reload */
    get traceScene(): TraceScene | null { return this._traceScene; }

    /**
     * Assigning a scene here is the editor's one "a scene is live and an
     * editor owns it" signal, so it is also where the editor-side scene
     * bridges get installed: the Layout size-bake → BEHAVIOR_CHANGED refresh,
     * and the layout topology sync that re-solves Layouts when children change
     * (settled again after every undo and redo). Core stays free of editor imports; a runtime viewer never
     * touches an UndoManager and so never subscribes.
     *
     * Feature-detected, not assumed: consumers assign partial TraceScene
     * stand-ins here (the sandbox's service tests do), and an assignment that
     * threw would take out unrelated editor wiring for a notification.
     */
    set traceScene(scene: TraceScene | null) {
        if (scene === this._traceScene) return;
        this._detachSceneBridges?.();
        this._detachSceneBridges = null;
        this._traceScene = scene;
        const detaches: Array<() => void> = [];
        if (typeof scene?.onLayoutSizeBaked === 'function') detaches.push(installLayoutInspectorRefresh(scene));
        if (scene && typeof scene.onTopologyChanged === 'function') detaches.push(installLayoutTopologySync(scene));
        if (detaches.length > 0) this._detachSceneBridges = () => detaches.forEach(detach => detach());
    }

    /** After a replay: an undo restores poses and children with no forward edit to re-solve the Layouts it touched. */
    private _settleAfterReplay(): void {
        const scene = this._traceScene;
        if (!scene || typeof scene.getSceneObjects !== 'function') return;
        // Never in place of the replay's own error.
        try {
            settleLayouts(scene);
        } catch (error) {
            Logger.warn(`[Undo] Layouts did not settle after a replay: ${String(error)}`);
        }
    }

    /**
     * Stack position at the last save. `isDirty === true` iff the current stack
     * position differs from this mark. Set by markSaved() / cleared by clear().
     * See Q3 in EDITOR_CORE_SHARING_PLAN.md.
     */
    private _saveMark: number | null = 0;

    /** Keyboard listener detach hook, set by attachKeyboardShortcuts(). */
    private _detachKeyboard: (() => void) | null = null;

    // No explicit constructor — keyboard shortcuts are opt-in via
    // attachKeyboardShortcuts() so tests don't leak capture-phase listeners.
    // Use getUndoManager() for the singleton, which auto-attaches.

    /** True while an undo/redo operation is executing (prevents re-entrant recording) */
    get isUndoRedoing(): boolean {
        return this._isUndoRedoing;
    }

    /** True when undo is frozen (e.g. during play mode) — pushes and undo/redo are ignored */
    get frozen(): boolean {
        return this._frozen;
    }

    /** Freeze the undo stack — all push/undo/redo calls are silently ignored */
    freeze(): void {
        this._frozen = true;
        Logger.debug('[Undo] Frozen (play mode)');
    }

    /** Unfreeze the undo stack */
    thaw(): void {
        this._frozen = false;
        Logger.debug('[Undo] Thawed (edit mode)');
    }

    /**
     * Push a new command. Clears the redo stack.
     * Attempts coalescing with the top of the undo stack first.
     */
    push(command: UndoCommand): void {
        if (this._isUndoRedoing || this._frozen) return;
        if (this._pendingPartial) {
            Logger.warn(`[Undo] Cannot push "${command.label}" while "${this._pendingPartial.command.label}" is partly applied`);
            this._notifyChange();
            return;
        }

        const top = this.undoStack[this.undoStack.length - 1];
        if (top?.coalesce?.(command)) {
            this.redoStack = [];
            Logger.debug(`[Undo] Coalesced: ${command.label} (stack: ${this.undoStack.length})`);
            this._notifyPush(top, true);
            this._notifyChange();
            return;
        }

        this.undoStack.push(command);
        this.redoStack = [];

        let shifted = false;
        if (this.undoStack.length > MAX_DEPTH) {
            this.undoStack.shift();
            shifted = true;
            // If the save mark pointed to a discarded entry, it becomes unreachable;
            // flag dirty forever until a new save clears it.
            if (this._saveMark !== null) {
                this._saveMark = this._saveMark - 1;
                if (this._saveMark < 0) this._saveMark = null;
            }
        }

        Logger.debug(`[Undo] Pushed: ${command.label} (stack: ${this.undoStack.length})`);
        this._notifyPush(command, false);
        this._notifyChange(shifted);
    }

    async undo(): Promise<void> {
        if (this._frozen) return;
        if (this._pendingPartial) return this._resolvePartial('undo');
        const command = this.undoStack.pop();
        if (!command) {
            Logger.debug('[Undo] Nothing to undo');
            return;
        }

        // Restore-on-failure: if `command.undo()` throws, put the command
        // back on the undo stack so the user's history isn't silently
        // corrupted. Without this, a failing undo would drop the command
        // entirely and leave no way to get back to the pre-failure state.
        Logger.debug(`[Undo] Undoing: ${command.label}`);
        this._isUndoRedoing = true;
        try {
            await command.undo();
            this.redoStack.push(command);
        } catch (err) {
            this.undoStack.push(command);
            if (command.isPartlyApplied?.()) this._pendingPartial = { command, stack: 'undo' };
            throw err;
        } finally {
            this._isUndoRedoing = false;
            this._notifyChange();
            this._settleAfterReplay();
        }
    }

    async redo(): Promise<void> {
        if (this._frozen) return;
        if (this._pendingPartial) return this._resolvePartial('redo');
        const command = this.redoStack.pop();
        if (!command) {
            Logger.debug('[Undo] Nothing to redo');
            return;
        }

        // Symmetric restore-on-failure: if `command.redo()` throws, put the
        // command back on the redo stack so the user can try again rather
        // than losing the operation from history entirely.
        Logger.debug(`[Undo] Redoing: ${command.label}`);
        this._isUndoRedoing = true;
        try {
            await command.redo();
            this.undoStack.push(command);
        } catch (err) {
            this.redoStack.push(command);
            if (command.isPartlyApplied?.()) this._pendingPartial = { command, stack: 'redo' };
            throw err;
        } finally {
            this._isUndoRedoing = false;
            this._notifyChange();
            this._settleAfterReplay();
        }
    }

    /** Clear both stacks (e.g. on scene load) and reset the save mark to "clean". */
    clear(): void {
        this.undoStack = [];
        this.redoStack = [];
        this._pendingPartial = null;
        this._saveMark = 0;
        this._notifyChange();
    }

    get canUndo(): boolean { return this._pendingPartial !== null || this.undoStack.length > 0; }
    get canRedo(): boolean { return this._pendingPartial !== null || this.redoStack.length > 0; }

    /** Current undo stack depth. Useful for external change tracking. */
    get undoDepth(): number { return this.undoStack.length; }

    /** The label of the step `undo()` would take back next, or null. */
    get undoLabel(): string | null { return this._pendingPartial?.command.label ?? this.undoStack[this.undoStack.length - 1]?.label ?? null; }

    /** The label of the step `redo()` would put back next, or null. */
    get redoLabel(): string | null { return this._pendingPartial?.command.label ?? this.redoStack[this.redoStack.length - 1]?.label ?? null; }

    private async _resolvePartial(direction: 'undo' | 'redo'): Promise<void> {
        const pending = this._pendingPartial;
        if (!pending) return;
        this._isUndoRedoing = true;
        try {
            await pending.command[direction]();
            if (pending.stack === direction) {
                const source = direction === 'undo' ? this.undoStack : this.redoStack;
                const destination = direction === 'undo' ? this.redoStack : this.undoStack;
                if (source[source.length - 1] !== pending.command) {
                    throw new Error(`[Undo] Partly applied command "${pending.command.label}" is not on top of its stack`);
                }
                source.pop();
                destination.push(pending.command);
            }
            this._pendingPartial = null;
        } catch (err) {
            if (!pending.command.isPartlyApplied?.()) this._pendingPartial = null;
            throw err;
        } finally {
            this._isUndoRedoing = false;
            this._notifyChange();
            this._settleAfterReplay();
        }
    }

    /**
     * Mark the current stack position as the last-saved state.
     * `isDirty` will be false until the stack moves away from here.
     */
    markSaved(): void {
        this._saveMark = this.undoStack.length;
        this._notifyChange();
    }

    /**
     * True iff the current stack position differs from the last save mark.
     * Also true if the save mark has been discarded by stack trimming
     * (meaning we can no longer prove we match the save state).
     */
    get isDirty(): boolean {
        return this._saveMark === null || this._saveMark !== this.undoStack.length;
    }

    /** Subscribe to undo/redo stack changes. Returns an unsubscribe function. */
    subscribe(callback: (depthShifted: boolean) => void): () => void {
        this._onChangeCallbacks.push(callback);
        return () => {
            const idx = this._onChangeCallbacks.indexOf(callback);
            if (idx >= 0) this._onChangeCallbacks.splice(idx, 1);
        };
    }

    private _notifyChange(depthShifted = false): void {
        for (const cb of this._onChangeCallbacks) cb(depthShifted);
    }

    /**
     * Subscribe to command pushes (including coalesced ones). The callback
     * receives the command that now occupies the top of the undo stack and
     * a `coalesced` flag indicating whether this push merged into the prior
     * command. Replay recorders use this to serialize commands at push time.
     * Returns an unsubscribe function.
     */
    subscribePush(cb: (cmd: UndoCommand, coalesced: boolean) => void): () => void {
        this._onPushCallbacks.push(cb);
        return () => {
            const idx = this._onPushCallbacks.indexOf(cb);
            if (idx >= 0) this._onPushCallbacks.splice(idx, 1);
        };
    }

    private _notifyPush(cmd: UndoCommand, coalesced: boolean): void {
        for (const cb of this._onPushCallbacks) cb(cmd, coalesced);
    }

    /**
     * Register global Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y keyboard listeners on
     * `window`. Idempotent — calling this a second time on the same instance
     * is a no-op.
     *
     * Call this explicitly when constructing a non-singleton `UndoManager`.
     * The singleton from `getUndoManager()` attaches automatically.
     */
    attachKeyboardShortcuts(): void {
        if (this._detachKeyboard) return;

        // Capture phase so we intercept before inputs handle their own Ctrl+Z.
        // All field edits are committed immediately via liveCommit, so browser-native
        // undo on inputs is not useful — our UndoManager owns the full undo stack.
        const handler = (e: KeyboardEvent): void => {
            const target = e.target as HTMLElement | null;
            if (target) {
                const tag = target.tagName;
                if (
                    target.isContentEditable
                    || tag === 'INPUT'
                    || tag === 'TEXTAREA'
                    || tag === 'SELECT'
                ) {
                    return;
                }
            }
            if (this.traceScene?.hasGUIFocus) {
                return;
            }

            const mod = e.ctrlKey || e.metaKey;
            if (!mod) return;

            if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
                e.preventDefault();
                void this.undo();
            } else if (
                (e.key.toLowerCase() === 'z' && e.shiftKey) ||
                (e.key.toLowerCase() === 'y')
            ) {
                e.preventDefault();
                void this.redo();
            }
        };

        window.addEventListener('keydown', handler, true);
        this._detachKeyboard = () => {
            window.removeEventListener('keydown', handler, true);
            this._detachKeyboard = null;
        };
    }

    /**
     * Remove any keyboard listeners previously registered by
     * `attachKeyboardShortcuts()`. Safe to call when no listener is attached.
     */
    detachKeyboardShortcuts(): void {
        this._detachKeyboard?.();
    }
}

/** Singleton instance */
let _undoManager: UndoManager | null = null;

/**
 * Return the shared UndoManager. The first call also wires up global
 * Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y keyboard shortcuts.
 *
 * ## ⚠️ Do not call from tests
 *
 * The returned instance is module-level and persists across test cases in
 * the same worker — calling this from test setup means:
 *
 *   - The undo stack is shared across unrelated tests (one test's pushes
 *     leak into the next test's starting state).
 *   - The global keyboard listener attached on first access never gets
 *     torn down between tests.
 *   - Parallel test files running in the same worker see each other's
 *     mutations.
 *
 * Tests that need an UndoManager should construct one directly with
 * `new UndoManager()`. That path skips keyboard attachment (see the class
 * header) and gives each test a fresh, isolated instance. Integration tests
 * that want to cover the keyboard path can call `attachKeyboardShortcuts()`
 * on their local instance and `detachKeyboardShortcuts()` in cleanup.
 *
 * There is intentionally no `resetUndoManager()` or similar escape hatch —
 * adding one would make it too easy for production code to accidentally
 * discard the user's undo history.
 */
export function getUndoManager(): UndoManager {
    if (!_undoManager) {
        _undoManager = new UndoManager();
        _undoManager.attachKeyboardShortcuts();
    }
    return _undoManager;
}
